/**
 * Триггер выпадающего фильтра: значок, подпись, шеврон. Один на «Проекты» и
 * «Категорию разговора», чтобы фильтры одной панели были одного вида (#2315).
 * Высота и вид — как у триггера `Select size="sm"`, поэтому фильтр стоит в
 * одном ряду с остальными контролами `sm`, не выбиваясь (#1360).
 *
 * Шеврон поворачивается по `data-state="open"`, который ставит Radix-триггер
 * (`asChild`): своего состояния открытия триггеру не нужно.
 */
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CONTROL_HEIGHT } from '@/components/ui/control-size';

interface FilterTriggerProps extends ComponentPropsWithoutRef<'button'> {
  icon: ReactNode;
  label: ReactNode;
}

export const FilterTrigger = forwardRef<HTMLButtonElement, FilterTriggerProps>(
  ({ icon, label, className, type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'group inline-flex items-center gap-2 rounded-input border border-border bg-bg-surface px-2 text-xs text-fg',
        CONTROL_HEIGHT.sm,
        'hover:border-border-emphasis transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      {...props}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-fg-muted">
        {icon}
      </span>
      {/* The label is cut to the bar's width: the full value on hover. */}
      <span
        className="min-w-0 flex-1 truncate text-left"
        title={typeof label === 'string' ? label : undefined}
      >
        {label}
      </span>
      <ChevronDown
        aria-hidden="true"
        className="h-4 w-4 shrink-0 text-fg-muted transition-transform group-data-[state=open]:rotate-180"
      />
    </button>
  )
);
FilterTrigger.displayName = 'FilterTrigger';
